import React from 'react';
import { EntityLink } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Link } from '@backstage/core-components';
import { IconComponent, useApp } from '@backstage/core-plugin-api';
import LanguageIcon from '@material-ui/icons/Language';
import { EntitySwitch } from '@backstage/plugin-catalog';
import { hasLinks } from '../utils';
import { Box, Grid, Typography, makeStyles } from '@material-ui/core';

const useStyles = makeStyles(theme => ({
  svgIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 'inherit',
  },
  // The groups sit side by side and wrap by width, each listing its links
  // below its heading - a group of one link takes one block, not a row.
  groups: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: theme.spacing(2, 3),
  },
}));

const WebLink = ({
  href,
  text,
  Icon,
}: {
  href: string;
  text?: string;
  Icon?: IconComponent;
}) => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center" py={0.5}>
      <Box mr={1} className={classes.svgIcon}>
        {Icon ? (
          <Icon fontSize="inherit" />
        ) : (
          <LanguageIcon fontSize="inherit" />
        )}
      </Box>
      <Box flexGrow="1">
        <Link to={href}>{text ?? href}</Link>
      </Box>
    </Box>
  );
};

// The groups the catalog tags links with in `type`, in the order the card
// shows them. A link without a type, or with one not listed here, is a general
// link. The tools of the clusters of all environments (`env:<env>`) share
// one group - their titles name the environment already.
const groupTitles = [
  { key: 'general', title: 'General' },
  { key: 'code', title: 'Code' },
  { key: 'cluster-tools', title: 'Kubernetes' },
  { key: 'security', title: 'Secrets & Security' },
];

type LinkGroup = { key: string; title: string; links: EntityLink[] };

const linkTitle = (link: EntityLink): string => link.title ?? link.url;

const groupKey = (link: EntityLink): string => {
  if (link.type?.startsWith('env:')) return 'cluster-tools';
  if (link.type === 'code' || link.type === 'security') return link.type;
  return 'general';
};

const groupLinks = (links: EntityLink[]): LinkGroup[] => {
  const byKey = new Map<string, EntityLink[]>();
  for (const link of links) {
    const key = groupKey(link);
    byKey.set(key, [...(byKey.get(key) ?? []), link]);
  }
  // Each group lists its links alphabetically, whatever order the entity
  // declares them in.
  return groupTitles
    .map(g => ({
      ...g,
      links: (byKey.get(g.key) ?? []).sort((a, b) =>
        linkTitle(a).localeCompare(linkTitle(b)),
      ),
    }))
    .filter(g => g.links.length > 0);
};

const LinksContent = () => {
  const { entity } = useEntity();
  const app = useApp();
  const classes = useStyles();

  const iconResolver = (key?: string): IconComponent =>
    key ? (app.getSystemIcon(key) ?? LanguageIcon) : LanguageIcon;

  const groups = groupLinks(entity.metadata.links ?? []);

  return (
    <InfoCard title="Links" variant="fullHeight">
      <Box className={classes.groups}>
        {groups.map(group => (
          <Box key={group.key}>
            <Typography
              variant="overline"
              color="textSecondary"
              display="block"
            >
              {group.title}
            </Typography>
            {group.links.map((link: EntityLink, i: number) => (
              <WebLink
                key={i}
                href={link.url}
                text={link.title}
                Icon={iconResolver(link.icon)}
              />
            ))}
          </Box>
        ))}
      </Box>
    </InfoCard>
  );
};

/**
 * Displays the entity's metadata links in the classic compact layout with a
 * divider under the title and icons at text height, as a full-width row of an
 * entity page's grid. Replaces Backstage's EntityLinksCard, which since 1.50
 * renders via the new @backstage/ui card (no divider, wider spacing, oversized
 * icons).
 *
 * Links are grouped by their `type` under a heading each, see groupLinks - also
 * when they all fall into one group, so the card reads the same on every page.
 * An entity without links gets no card, and no empty grid row either.
 *
 * @public
 */
export const RTLLinksCard = () => (
  <EntitySwitch>
    <EntitySwitch.Case if={hasLinks}>
      <Grid item xs={12}>
        <LinksContent />
      </Grid>
    </EntitySwitch.Case>
  </EntitySwitch>
);
